/**
 * @file ほかの端末と同じページを開く連動で、ほかの端末が開いた本とページへついていく hook である。
 *
 * @what: 連動を入れていて、まなぶ人が決まっているときだけ、まなぶ人の知らせの doc
 *   (`families/{fid}/study_reading_sync/{personId}`) を購読する。時刻が変わったら、自分の送信が
 *   終わるのを待ってから閲覧位置の一覧を取り直し、ついていく先 (model/deviceSync.ts) があれば
 *   onFollow に渡す。購読を始めた直後の知らせは、いまの状態として受け取るだけでついていかない。
 * @why: 閲覧位置の中身はサーバで暗号化しており、端末は Firestore から直接読めない。知らせの doc は
 *   時刻しか持たないので、変化を受けたら API から取り直す (案 A、代表の判断、2026-10-10)。
 *   購読の直後についていかないのは、本を開いた瞬間に、ほかの端末が前に開いた本へ連れていかれないためである。
 *   本を読む画面 (ページで読む) と本だなだけが使う。※なぜなら入力の途中の画面を切り替えると、
 *   書きかけを失うためである。
 * @failure: 購読の失敗 (rules の反映前の権限エラーなど) と一覧の取得の失敗は console に残すだけで、
 *   ついていかない (読む操作は止めない)。
 */

import { useEffect, useRef } from "react";
import { Timestamp, doc, onSnapshot } from "firebase/firestore";
import { getFirebaseFirestore } from "@/shared/firebase/client";
import { fetchReadingPositions } from "../api/readingPositions";
import type { StudySource } from "../api/sources";
import {
  deviceSyncId,
  deviceSyncSeenAt,
  markDeviceSyncFollowed,
  noteDeviceSyncSeen,
  useDeviceSyncEnabled,
  waitDeviceSyncSends,
} from "../lib/deviceSync";
import { deviceSyncTarget, type DeviceSyncTarget } from "../model/deviceSync";

export function useDeviceSync({
  familyId,
  learnerPersonId,
  current,
  sources,
  onFollow,
}: {
  familyId: string;
  learnerPersonId: string | null;
  /** いま開いている本とページ (本だなにいるときは null)。 */
  current: { sourceId: string; page: number | null } | null;
  sources: readonly StudySource[];
  onFollow: (target: DeviceSyncTarget) => void;
}): void {
  const enabled = useDeviceSyncEnabled();
  // 購読は家族とまなぶ人で張り直し、ページや本の一覧が変わるたびには張り直さない。
  const latest = useRef({ current, sources, onFollow });
  latest.current = { current, sources, onFollow };

  useEffect(() => {
    if (!enabled || !learnerPersonId) return;
    const controller = new AbortController();
    // 購読を始める前の知らせは、いまの状態として受け取るだけにする (端末の覚えから出た古いものも含む)
    let baselined = false;
    let running = false;
    let again = false;

    const check = async () => {
      if (running) {
        again = true;
        return;
      }
      running = true;
      try {
        do {
          again = false;
          // 自分の送信が届く前に比べると、あとから届いた自分の送信と食い違うので待つ
          await waitDeviceSyncSends();
          if (controller.signal.aborted) return;
          const positions = await fetchReadingPositions(
            familyId,
            learnerPersonId,
            controller.signal,
          );
          if (controller.signal.aborted) return;
          const target = deviceSyncTarget(positions, {
            myDeviceId: deviceSyncId(),
            seenAt: deviceSyncSeenAt(),
            current: latest.current.current,
            sources: latest.current.sources,
          });
          if (target) {
            noteDeviceSyncSeen(target.at);
            markDeviceSyncFollowed(target.sourceId, target.page);
            latest.current.onFollow(target);
          }
        } while (again && !controller.signal.aborted);
      } catch (err) {
        if (!controller.signal.aborted)
          console.warn("[study] failed to follow the other device", err);
      } finally {
        running = false;
      }
    };

    const ref = doc(
      getFirebaseFirestore(),
      "families",
      familyId,
      "study_reading_sync",
      learnerPersonId,
    );
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (!baselined) {
          // 知らせの時刻までの閲覧位置は見たことにする (購読の前に開かれた本へついていかない)
          const at: unknown = snap.get("updated_at");
          if (at instanceof Timestamp) noteDeviceSyncSeen(at.toMillis());
          if (!snap.metadata.fromCache) baselined = true;
          return;
        }
        void check();
      },
      (err) => {
        console.warn("[study] failed to watch the other devices", err);
      },
    );
    return () => {
      controller.abort();
      unsubscribe();
    };
  }, [enabled, familyId, learnerPersonId]);
}
